import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { HOME_PHOTO_PREVIEW } from "@/data/config";

/**
 * Editorial 3-photo teaser for the homepage. Uses an asymmetric composition:
 * one tall lead photograph on the left, two stacked photographs on the right.
 * Funnels visitors to /photos — this is NOT the full gallery.
 */
export default function HomePhotoPreview() {
  const [lead, ...rest] = HOME_PHOTO_PREVIEW;

  return (
    <section
      className="relative bg-black text-white py-16 md:py-28 border-t border-white/10"
      data-testid="home-photo-preview"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-14 items-end">
          <div className="col-span-12 md:col-span-7">
            <div className="eyebrow mb-4">§ Portfolio</div>
            <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight">
              A quiet look at the work.
              <br />
              <span className="italic text-zinc-400">The full archive lives on Photos.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:pl-8 flex md:justify-end">
            <Link
              to="/photos"
              data-testid="home-view-all-photos"
              className="btn-ghost inline-flex items-center gap-2"
            >
              View All Photos
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.4} />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-3 md:gap-5">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, ease: [0.7, 0, 0.2, 1] }}
            className="col-span-12 md:col-span-7 relative overflow-hidden bg-zinc-900 border border-white/10 aspect-[3/4] md:aspect-[4/5]"
          >
            <img
              src={lead.src}
              alt={lead.caption}
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
              <div className="eyebrow text-white/60 text-[10px]">{lead.tag}</div>
              <div className="font-serif text-xl md:text-2xl mt-1">{lead.caption}</div>
            </div>
          </motion.div>

          <div className="col-span-12 md:col-span-5 grid grid-cols-1 gap-3 md:gap-5">
            {rest.map((p, i) => (
              <motion.div
                key={p.src}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: 0.1 + i * 0.08, ease: [0.7, 0, 0.2, 1] }}
                className="relative overflow-hidden bg-zinc-900 border border-white/10 aspect-[4/3] md:aspect-[5/4]"
              >
                <img
                  src={p.src}
                  alt={p.caption}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                  <div className="eyebrow text-white/60 text-[10px]">{p.tag}</div>
                  <div className="font-serif text-base md:text-lg mt-1">{p.caption}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
